// Per-dialect synthesizer for CREATE / ALTER / DROP SEQUENCE (v2 Slice D3).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice D3.
//
// Per-engine surface:
//   - mssql:  CREATE SEQUENCE [schema.]name [AS <type>] [START WITH n]
//             [INCREMENT BY n] [{MINVALUE n | NO MINVALUE}]
//             [{MAXVALUE n | NO MAXVALUE}] [{CYCLE | NO CYCLE}]
//             [{CACHE n | NO CACHE}]
//             ALTER SEQUENCE name <change-clauses>
//             DROP SEQUENCE [IF EXISTS] name
//   - pgsql:  CREATE SEQUENCE [IF NOT EXISTS] [schema.]name [AS <type>]
//             [START WITH n] [INCREMENT BY n] [{MINVALUE n | NO MINVALUE}]
//             [{MAXVALUE n | NO MAXVALUE}] [{CYCLE | NO CYCLE}] [CACHE n]
//             [OWNED BY <table.col | NONE>]
//             ALTER SEQUENCE [IF EXISTS] name <change-clauses>
//             DROP SEQUENCE [IF EXISTS] name
//   - mysql:  unsupported — use AUTO_INCREMENT (op:'add-column' identity).
//   - oracle: CREATE SEQUENCE [schema.]name [START WITH n] [INCREMENT BY n]
//             [{MINVALUE n | NOMINVALUE}] [{MAXVALUE n | NOMAXVALUE}]
//             [{CYCLE | NOCYCLE}] [{CACHE n | NOCACHE}] [{ORDER | NOORDER}]
//             [{KEEP | NOKEEP}] [{SCALE [EXTEND] | NOSCALE}]
//             [SHARING = {METADATA | NONE}]
//             ALTER SEQUENCE name <change-clauses>
//             DROP SEQUENCE name  (no IF EXISTS)
//   - sqlite: unsupported — use INTEGER PRIMARY KEY AUTOINCREMENT.

import type { QualifiedName, SqlEngine } from '../../../sql-grammar/model';
import type { SequenceChange, SequenceDef } from '../../types';
import { DdlEditError } from '../../types';

import { maybeQuoteIdent } from './dialect';

function sequenceName(engine: SqlEngine, name: QualifiedName): string {
  if (name.raw && name.raw.length > 0) return name.raw;
  const bare = maybeQuoteIdent(engine, name.name, name.quoted);
  if (name.schema) {
    const schema = maybeQuoteIdent(engine, name.schema, name.quoted);
    return `${schema}.${bare}`;
  }
  return bare;
}

function rejectField(engine: SqlEngine, op: string, field: string): never {
  throw new Error(
    `applyDdlEdit: ${engine} ${op} does not accept '${field}'`,
  );
}

function unsupported(engine: SqlEngine, op: string, hint: string): never {
  throw new DdlEditError(
    'engine-does-not-support-op',
    `applyDdlEdit: ${engine} does not support ${op} — ${hint}`,
  );
}

const MYSQL_HINT
  = `mysql has no sequences. Use AUTO_INCREMENT columns instead `
    + `(op:'add-column' with IdentitySpec).`;
const SQLITE_HINT
  = `sqlite has no sequences. Use INTEGER PRIMARY KEY AUTOINCREMENT instead.`;

// ─── CREATE SEQUENCE ──────────────────────────────────────────────────────

export function synthesizeCreateSequence(
  engine: SqlEngine,
  seq: SequenceDef,
): string {
  if (engine === 'mysql') unsupported('mysql', 'create-sequence', MYSQL_HINT);
  if (engine === 'sqlite') unsupported('sqlite', 'create-sequence', SQLITE_HINT);
  if (engine === 'mssql') return synthMssqlCreate(seq);
  if (engine === 'postgres') return synthPgsqlCreate(seq);
  return synthOracleCreate(seq);
}

function synthMssqlCreate(seq: SequenceDef): string {
  if (seq.ifNotExists) {
    rejectField('mssql', 'create-sequence', 'ifNotExists');
  }
  if (seq.pgsqlOwnedBy) {
    rejectField('mssql', 'create-sequence', 'pgsqlOwnedBy');
  }
  rejectOracleOnlyFields('mssql', seq);
  let s = `CREATE SEQUENCE ${sequenceName('mssql', seq.name)}`;
  if (seq.mssqlAs) s += ` AS ${seq.mssqlAs}`;
  s += emitCommonNumeric(seq);
  s += emitMinMax(seq, 'mssql');
  s += emitCycle(seq, 'mssql');
  s += emitCache(seq, 'mssql');
  return s;
}

function synthPgsqlCreate(seq: SequenceDef): string {
  rejectOracleOnlyFields('postgres', seq);
  let s = 'CREATE SEQUENCE ';
  if (seq.ifNotExists) s += 'IF NOT EXISTS ';
  s += sequenceName('postgres', seq.name);
  if (seq.mssqlAs) s += ` AS ${seq.mssqlAs}`;
  s += emitCommonNumeric(seq);
  s += emitMinMax(seq, 'postgres');
  s += emitCycle(seq, 'postgres');
  s += emitCache(seq, 'postgres');
  if (seq.pgsqlOwnedBy) s += ` OWNED BY ${seq.pgsqlOwnedBy}`;
  return s;
}

function synthOracleCreate(seq: SequenceDef): string {
  if (seq.mssqlAs) rejectField('oracle', 'create-sequence', 'mssqlAs');
  if (seq.ifNotExists) rejectField('oracle', 'create-sequence', 'ifNotExists');
  if (seq.pgsqlOwnedBy) rejectField('oracle', 'create-sequence', 'pgsqlOwnedBy');
  let s = `CREATE SEQUENCE ${sequenceName('oracle', seq.name)}`;
  s += emitCommonNumeric(seq);
  s += emitMinMax(seq, 'oracle');
  s += emitCycle(seq, 'oracle');
  s += emitCache(seq, 'oracle');
  if (seq.oracleOrder !== undefined) s += seq.oracleOrder ? ' ORDER' : ' NOORDER';
  if (seq.oracleKeep !== undefined) s += seq.oracleKeep ? ' KEEP' : ' NOKEEP';
  if (seq.oracleScale !== undefined) {
    if (seq.oracleScale) {
      s += seq.oracleScaleExtend ? ' SCALE EXTEND' : ' SCALE';
    } else {
      s += ' NOSCALE';
    }
  }
  if (seq.oracleSharing) {
    s += ` SHARING = ${seq.oracleSharing.toUpperCase()}`;
  }
  return s;
}

function rejectOracleOnlyFields(engine: SqlEngine, seq: SequenceDef): void {
  if (seq.oracleOrder !== undefined) {
    rejectField(engine, 'create-sequence', 'oracleOrder');
  }
  if (seq.oracleKeep !== undefined) {
    rejectField(engine, 'create-sequence', 'oracleKeep');
  }
  if (seq.oracleScale !== undefined) {
    rejectField(engine, 'create-sequence', 'oracleScale');
  }
  if (seq.oracleScaleExtend !== undefined) {
    rejectField(engine, 'create-sequence', 'oracleScaleExtend');
  }
  if (seq.oracleSharing) {
    rejectField(engine, 'create-sequence', 'oracleSharing');
  }
}

function emitCommonNumeric(seq: SequenceDef): string {
  let s = '';
  if (seq.startWith !== undefined) s += ` START WITH ${seq.startWith}`;
  if (seq.incrementBy !== undefined) s += ` INCREMENT BY ${seq.incrementBy}`;
  return s;
}

function emitMinMax(seq: SequenceDef, engine: SqlEngine): string {
  const noMin = engine === 'oracle' ? 'NOMINVALUE' : 'NO MINVALUE';
  const noMax = engine === 'oracle' ? 'NOMAXVALUE' : 'NO MAXVALUE';
  let s = '';
  if (seq.minValue === null) s += ` ${noMin}`;
  else if (seq.minValue !== undefined) s += ` MINVALUE ${seq.minValue}`;
  if (seq.maxValue === null) s += ` ${noMax}`;
  else if (seq.maxValue !== undefined) s += ` MAXVALUE ${seq.maxValue}`;
  return s;
}

function emitCycle(seq: SequenceDef, engine: SqlEngine): string {
  if (seq.cycle === undefined) return '';
  const noCycle = engine === 'oracle' ? 'NOCYCLE' : 'NO CYCLE';
  return seq.cycle ? ' CYCLE' : ` ${noCycle}`;
}

function emitCache(seq: SequenceDef, engine: SqlEngine): string {
  if (seq.cache === undefined) return '';
  if (seq.cache === null) {
    // pgsql has no `NO CACHE` clause; emit `CACHE 1` (effectively disables
    // batched caching). mssql + oracle accept `NO CACHE` / `NOCACHE`.
    if (engine === 'postgres') return ' CACHE 1';
    return engine === 'oracle' ? ' NOCACHE' : ' NO CACHE';
  }
  return ` CACHE ${seq.cache}`;
}

// ─── ALTER SEQUENCE ───────────────────────────────────────────────────────

export function synthesizeAlterSequence(
  engine: SqlEngine,
  name: QualifiedName,
  change: SequenceChange,
): string {
  if (engine === 'mysql') unsupported('mysql', 'alter-sequence', MYSQL_HINT);
  if (engine === 'sqlite') unsupported('sqlite', 'alter-sequence', SQLITE_HINT);
  rejectAlterFieldsOnNonOracle(engine, change);
  const clauses = buildAlterClauses(engine, change);
  if (clauses.length === 0) {
    throw new Error(
      `applyDdlEdit: ${engine} alter-sequence requires at least one change `
        + `field set (incrementBy, minValue, maxValue, cycle, cache, `
        + `restartWith${engine === 'oracle' ? ', oracleOrder, oracleKeep' : ''})`,
    );
  }
  return `ALTER SEQUENCE ${sequenceName(engine, name)}${clauses.join('')}`;
}

function rejectAlterFieldsOnNonOracle(
  engine: SqlEngine,
  change: SequenceChange,
): void {
  if (engine === 'oracle') return;
  if (change.oracleOrder !== undefined) {
    rejectField(engine, 'alter-sequence', 'oracleOrder');
  }
  if (change.oracleKeep !== undefined) {
    rejectField(engine, 'alter-sequence', 'oracleKeep');
  }
}

function buildAlterClauses(
  engine: SqlEngine,
  change: SequenceChange,
): string[] {
  const out: string[] = [];
  if (change.incrementBy !== undefined) {
    out.push(` INCREMENT BY ${change.incrementBy}`);
  }
  out.push(emitMinMax({ minValue: change.minValue, maxValue: change.maxValue } as SequenceDef, engine));
  out.push(emitCycle({ cycle: change.cycle } as SequenceDef, engine));
  out.push(emitCache({ cache: change.cache } as SequenceDef, engine));
  if (change.restartWith !== undefined) {
    // oracle: 12c+ accepts `RESTART START WITH n`; mssql + pgsql: `RESTART
    // WITH n`. The synthesizer normalizes oracle to `RESTART START WITH`
    // for compatibility with the documented 12c+ matrix.
    if (engine === 'oracle') out.push(` RESTART START WITH ${change.restartWith}`);
    else out.push(` RESTART WITH ${change.restartWith}`);
  }
  if (engine === 'oracle') {
    if (change.oracleOrder !== undefined) {
      out.push(change.oracleOrder ? ' ORDER' : ' NOORDER');
    }
    if (change.oracleKeep !== undefined) {
      out.push(change.oracleKeep ? ' KEEP' : ' NOKEEP');
    }
  }
  return out.filter((c) => c.length > 0);
}

// ─── DROP SEQUENCE ────────────────────────────────────────────────────────

export function synthesizeDropSequence(
  engine: SqlEngine,
  name: QualifiedName,
  ifExists: boolean,
): string {
  if (engine === 'mysql') unsupported('mysql', 'drop-sequence', MYSQL_HINT);
  if (engine === 'sqlite') unsupported('sqlite', 'drop-sequence', SQLITE_HINT);
  if (ifExists && engine === 'oracle') {
    rejectField('oracle', 'drop-sequence', 'ifExists');
  }
  let s = 'DROP SEQUENCE ';
  if (ifExists) s += 'IF EXISTS ';
  s += sequenceName(engine, name);
  return s;
}
